import React, { useContext } from 'react';
import { SupraAccessBenefitSVG } from '@bb/elements-web';
import {
  SupraSubscriptionProductBillingModel,
  SupraSystemSubscriptionStatus,
  SupraSystemSubscriptionType,
} from '@bb/domain/dist/types';
import { camelCase, startCase } from 'lodash';
import { AuthContext } from 'contexts';
import SubscriptionCard, {
  SubscriptionCardConfigType,
} from './SubscriptionCard';

export interface SupraOneCardProps {
  /** Whether there was an error fetching who pays for the Supra One subscription. */
  fetchDataError?: boolean;
  /** How the subscription is billed - affects text next to status pill */
  supraSubscriptionPayer?: SupraSubscriptionProductBillingModel;
  /** Callback when button is clicked */
  onButtonClick?: () => void;
}

type StatusConfig = SubscriptionCardConfigType & {
  /** Whether to treat as subscribed for child card styling */
  isSubscribed: boolean;
  /** Whether we should intentionally disable the button. */
  isButtonDisabled: boolean;
};

interface SuspensionValues {
  isSuspendedVal: boolean;
  suspensionTextVal: string;
}

/** Statuses that represent an existing subscription relationship (any state except fully unsubscribed) */
const ACTIVE_SUPRA_STATUSES: readonly SupraSystemSubscriptionStatus[] = [
  SupraSystemSubscriptionStatus.subscribed,
  SupraSystemSubscriptionStatus.pending,
  SupraSystemSubscriptionStatus.suspended,
  SupraSystemSubscriptionStatus.deactivated,
] as const;

/**
 * Hook that reads the user's Supra One subscription status from auth context.
 * Exported so parent sections can determine whether to render this card
 * without needing the status drilled down as a prop.
 */
export const useSupraSubscriptionStatus = (): SupraSystemSubscriptionStatus => {
  const { auth } = useContext(AuthContext);
  return (
    auth.supraSubscription?.status ?? SupraSystemSubscriptionStatus.unsubscribed
  );
};

const DEFAULT_DESCRIPTION =
  'Access properties and manage your Supra lockboxes directly from your mobile device.';

const UNSUBSCRIBED_DESCRIPTION =
  'Access properties and manage your lockboxes directly from your device.\nReach out to your organization to subscribe.';

/** Determine the suspended values we will add to the config. */
const getSuspensionValues = (
  isUserSuspended: boolean,
  supraSubscriptionPayer: SupraSubscriptionProductBillingModel,
): SuspensionValues => {
  // If a keyholder has not paid for their own subscriptions, return that.
  if (
    supraSubscriptionPayer === SupraSubscriptionProductBillingModel.Keyholder &&
    isUserSuspended
  ) {
    return {
      isSuspendedVal: true,
      suspensionTextVal:
        'This subscription has been suspended due to outstanding payments. Please update your card information.',
    };
  }
  // Otherwise, the keyholder is in good standing.
  return {
    isSuspendedVal: false,
    suspensionTextVal: '',
  };
};

/** Define what the contents of the card should be */
const getStatusConfig = (
  status: SupraSystemSubscriptionStatus,
  supraSubscriptionType?: SupraSystemSubscriptionType,
): StatusConfig => {
  const subscribedPlanName = `Supra Access - ${startCase(camelCase(supraSubscriptionType))} Key`;

  // eslint-disable-next-line default-case
  switch (status) {
    case SupraSystemSubscriptionStatus.subscribed:
      return {
        planName: subscribedPlanName,
        description: DEFAULT_DESCRIPTION,
        statusText: 'Active',
        isUpgradeAvailable: false,
        isSubscribed: true,
        isButtonDisabled: false,
      };
    case SupraSystemSubscriptionStatus.pending:
      return {
        planName: 'Supra Access',
        description: DEFAULT_DESCRIPTION,
        statusText: 'Pending',
        isUpgradeAvailable: false,
        isSubscribed: true,
        isButtonDisabled: true,
      };
    // TODO: KB-1069 remove these two unused status configs (deactivated and suspended)
    case SupraSystemSubscriptionStatus.suspended:
      return {
        planName: subscribedPlanName,
        description: DEFAULT_DESCRIPTION,
        statusText: 'Suspended',
        isUpgradeAvailable: false,
        isSubscribed: true,
        isButtonDisabled: true,
      };
    case SupraSystemSubscriptionStatus.deactivated:
      return {
        planName: 'Supra Access',
        description: UNSUBSCRIBED_DESCRIPTION,
        statusText: 'Deactivated',
        isUpgradeAvailable: true,
        isSubscribed: false,
        isButtonDisabled: true,
      };
    case SupraSystemSubscriptionStatus.unsubscribed:
      return {
        planName: 'Supra Access',
        description: UNSUBSCRIBED_DESCRIPTION,
        statusText: '',
        isUpgradeAvailable: true,
        isSubscribed: false,
        isButtonDisabled: false,
      };
  }
};

/** Resolves the text shown next to the status pill */
const getManagedByText = (
  status: SupraSystemSubscriptionStatus,
  payer?: SupraSubscriptionProductBillingModel,
): string => {
  if (status === SupraSystemSubscriptionStatus.pending) {
    return 'Check email for next steps';
  }

  if (!payer) {
    return '';
  }

  const billingTypeLabels: Record<
    SupraSubscriptionProductBillingModel,
    string
  > = {
    Board: 'Managed by Organization',
    User: '',
  };

  return billingTypeLabels[payer];
};

/**
 * The card displaying information about the user's
 * Supra One subscription. (Or lack thereof.)
 */
const SupraOneCard: React.FC<SupraOneCardProps> = ({
  fetchDataError,
  supraSubscriptionPayer,
  onButtonClick,
}) => {
  const { auth } = useContext(AuthContext);
  const status = useSupraSubscriptionStatus();

  // Grab the subscription type if the user has an active record.
  const hasSupraSubscriptionRecord = ACTIVE_SUPRA_STATUSES.includes(status);
  const supraSubscriptionType = hasSupraSubscriptionRecord
    ? auth.supraSubscription.type
    : null;

  const handleClick = () => {
    onButtonClick?.();
  };

  /** Whether the user viewing this page is suspended for an unpaid balance. */
  const isUserSuspended = !auth.supraPaid && !auth.isInBillingCollection;

  const suspensionValues = getSuspensionValues(
    isUserSuspended,
    supraSubscriptionPayer,
  );

  const config = getStatusConfig(status, supraSubscriptionType);
  config.isSuspended = suspensionValues.isSuspendedVal;
  config.suspensionTooltipText = suspensionValues.suspensionTextVal;
  const managedByText = getManagedByText(status, supraSubscriptionPayer);

  return (
    <SubscriptionCard
      fetchDataError={fetchDataError}
      isSubscribed={config.isSubscribed}
      allowedToSelfUpgrade={false}
      statusText={config.statusText}
      image={<SupraAccessBenefitSVG />}
      isUpgradeAvailable={config.isUpgradeAvailable}
      isButtonDisabled={config.isButtonDisabled}
      isSuspended={config.isSuspended}
      suspensionTooltipText={config.suspensionTooltipText}
      managedByText={managedByText}
      upgradeText="Subscribe by contacting your organization"
      planName={config.planName}
      description={config.description}
      onButtonClick={handleClick}
    />
  );
};

export default SupraOneCard;
